/**
 * BỘ SKILL MỞ RỘNG — 3 chiêu MỚI mỗi class (15 chiêu), ghép bằng MẢNH.
 *
 * VÌ SAO TÁCH RA FILE RIÊNG (không nhét vào `classes.ts`):
 * `classes.ts` là "7 chiêu gốc" đang được `World.castSkill()` đánh theo INDEX
 * (`cls.skills[index]`, `hero.skillCds[index]`). Nếu thêm thẳng chiêu vào mảng đó thì
 * mọi save cũ, mọi vòng hồi chiêu và mọi test cân bằng đều lệch index. Tách ra + để
 * người chơi TỰ GHÉP rồi TỰ XẾP vào ô (xem `sim/craft.ts`) là cách duy nhất giữ
 * 7 chiêu gốc bất biến.
 *
 * ⚠️ BẪY 1 — `import type { SkillDef }`: chỉ lấy KIỂU. `classes.ts` không được phép
 * import ngược file này (sẽ thành import vòng, `CLASSES` là `undefined` lúc eval module),
 * nên ở đây TUYỆT ĐỐI không đọc `CLASSES` ở cấp module — chỉ đọc trong hàm.
 *
 * ⚠️ BẪY 2 — `anim` phải là id CÓ THẬT trong `CAST_ANIMS` (`src/data/skill-anims.ts`).
 * Gõ sai một chữ thì `animOf()` rơi về mặc định và chiêu diễn sai dáng mà KHÔNG có lỗi
 * nào hiện ra. `tests/craft.test.ts` khoá lại: mọi `anim` của 10 chiêu/class phải có
 * trong `CAST_ANIMS`.
 *
 * ⚠️ BẪY 3 — `unlockWave`: chiêu gốc mở dần theo wave (1→10) để dạy người chơi.
 * Chiêu GHÉP thì người chơi đã trả mảnh để có nó rồi ⇒ để `unlockWave: 1` (dùng được
 * ngay khi trang bị), KHÔNG bắt chờ tới wave 10.
 */
import type { SkillDef } from './classes';
import { CLASSES, CLASS_ORDER } from './classes';
import type { ClassId } from '../sim/types';

/** Trần số chiêu trang bị được cho 1 class — đúng bằng số ô UI đang có. */
/**
 * SỐ CHIÊU TỐI ĐA MỘT TƯỚNG ĐƯỢC MANG.
 *
 * Người dùng chốt: *"giờ nhân vật chỉ được mang tối đa 3 skill thôi"* (trước là 7).
 * ⚠️ Đổi Ở ĐÂY là đủ cho cả UI lẫn sim: `validateLoadout`/ `equipSkill` (sim/craft.ts) và
 * `renderSkill` (hud.ts) đều đọc chung hằng số này — không có chỗ nào hard-code số 7.
 */
export const MAX_EQUIPPED_SKILLS = 3;

/**
 * 3 CHIÊU MỚI MỖI CLASS — đúng schema `SkillDef`, `kind` chỉ dùng giá trị đang có.
 *
 * QUY TẮC CHỌN SỐ LIỆU (để không thành "bản sao mạnh hơn" của chiêu gốc):
 *  · Không chiêu mới nào rẻ hơn + mạnh hơn + hồi nhanh hơn cùng lúc so với chiêu gốc
 *    cùng vai. Mỗi chiêu mới ĐỔI một trục: rẻ hơn nhưng yếu hơn, hoặc mạnh hơn nhưng
 *    đắt/ hồi lâu hơn.
 *  · Mỗi class giữ đúng "chất" của mình: Dân tập gym = chịu đòn + nổ quanh người;
 *    Gái ngành = cơ động; Dân chợ búa = khống chế diện rộng; Bắn thuê = tầm xa;
 *    Đẻ mướn = đẻ quân + hồi máu.
 *  · `desc` ghi SỐ cụ thể để người chơi so sánh được, không nói chung chung.
 */
/**
 * 15 CHIÊU GHÉP theo class.
 *
 * ⚠️ `Partial` (không phải `Record` đầy đủ): 2 class MỚI (Thợ mộc · Thợ lặn) chưa có chiêu ghép —
 * người dùng chốt *"design, skill, mọi thứ mình sẽ làm sau"*. Mọi chỗ đọc phải chịu được `undefined`
 * (đã kiểm: `craftableSkills`/ `skillPool`/ UI đều dùng `?? []` hoặc `Object.values`).
 */
export const EXTRA_SKILLS: Partial<Record<ClassId, SkillDef[]>> = {
  // ------------------------------------------------------------------ Dân tập gym
  strength: [
    {
      id: 'kimCangChuong',
      name: 'Chưởng Gồng Cơ',
      desc: 'Chưởng gồng cơ: 300% sát thương trong 140px quanh người',
      kind: 'aoeDamage',
      manaCost: 40, cooldown: 11, unlockWave: 1,
      // 3.0 × 140px: mạnh hơn Thiết Quyền (2.6/110) nhưng đắt hơn (40 vs 35) và hồi lâu
      // hơn (11 vs 8) ⇒ dùng để dọn bầy, không phải để bấm liên tục.
      mult: 3, radius: 140, maxTargets: 8,
      anim: 'palmPush',
    },
    {
      id: 'hoTheLaHan',
      name: 'Đai Lưng Bảo Hộ',
      desc: 'Khiên 35% máu tối đa trong 7s (rẻ, hồi nhanh)',
      kind: 'shield',
      manaCost: 45, cooldown: 16, unlockWave: 1,
      // Cố ý YẾU hơn La Hán Khiên (45%/8s) nhưng hồi nhanh hơn (16 vs 18) ⇒ chiêu
      // "đỡ đòn rẻ" cho người chơi chưa đủ mana cho chiêu gốc.
      pct: 0.35, duration: 7,
      anim: 'guard',
    },
    {
      id: 'suTuHong',
      name: 'Hét Phòng Gym',
      desc: 'Hét vang phòng tập: giảm 25% sát thương quái trong 220px, 6s',
      kind: 'debuff',
      manaCost: 35, cooldown: 15, unlockWave: 1,
      // Dân tập gym không có chiêu khống chế sát thương nào (chỉ có Chửi Đổng kéo quái) ⇒
      // bù bằng debuff atk 0.75. Yếu hơn Đả Kích Tinh Thần của Dân chợ búa (0.65) để
      // không cướp nghề của class đó.
      stat: 'atk', buffMult: 0.75, duration: 6, radius: 220, maxTargets: 99,
      anim: 'shout',
    },
  ],

  // ------------------------------------------------------------------ Gái ngành
  agility: [
    {
      id: 'loiDinhCuoc',
      name: 'Xoay Gót Sen',
      desc: 'Xoay gót sen một vòng: 220% sát thương trong 100px',
      kind: 'aoeDamage',
      manaCost: 30, cooldown: 8, unlockWave: 1,
      // So với Vô Ảnh Cước (2.4/80px/25 mana/7s): nhỉnh hơn về sát thương + bán kính
      // nhưng đắt và chậm hơn ⇒ hai chiêu bổ sung nhau, không thay thế.
      mult: 2.2, radius: 100, maxTargets: 6,
      anim: 'dragonKick',
    },
    {
      id: 'phanThanCham',
      name: 'Lướt Tới Bên',
      desc: 'Lướt tới bên người ta: 240% sát thương, xa 260px',
      kind: 'dashStrike',
      manaCost: 30, cooldown: 12, unlockWave: 1,
      // Ảnh Bộ: 220px / 200% / 25 mana / 9s. Bản này xa hơn + mạnh hơn nhưng hồi lâu hơn.
      // Dáng 'spin' (quét ngang nhiều nhịp) để NHÌN là phân biệt được với 'dash' của Ảnh Bộ.
      distance: 260, mult: 2.4,
      anim: 'spin',
    },
    {
      id: 'vanDaiBo',
      name: 'Lụa Bay',
      desc: 'Lụa bay theo từng bước: +35% tốc chạy trong 8s',
      kind: 'buff',
      manaCost: 35, cooldown: 18, unlockWave: 1,
      // Khinh Công (chiêu gốc) tăng tốc ĐÁNH; chiêu này tăng tốc CHẠY ⇒ dùng để chạy
      // cứu Nhà, vai trò khác hẳn nên không đè nhau.
      stat: 'moveSpeed', buffMult: 1.35, duration: 8,
      anim: 'dash',
    },
  ],

  // ------------------------------------------------------------------ Dân chợ búa
  street: [
    {
      id: 'tungHoaDiem',
      name: 'Tung Hỏa Diệm',
      desc: 'Tung 5 nắm lửa, mỗi nắm 140%, tầm 340px',
      kind: 'projectile',
      manaCost: 40, cooldown: 12, unlockWave: 1,
      // Ném Dép (4×120%) và Mưa Rau Củ (8×85%) đều là "nhiều món, mỗi món nhẹ".
      // Chiêu này: ít món hơn nhưng mỗi món NẶNG hơn ⇒ hợp với đám đông máu dày.
      mult: 1.4, count: 5, spread: 1,
      projectileSpeed: 540,
      // Khai `range` để bot auto biết tầm ném (không khai thì lấy tầm đánh thường).
      range: 340,
      anim: 'volley',
    },
    {
      id: 'khauChienQuanHung',
      name: 'Khẩu Chiến Quần Hùng',
      desc: 'Chửi một tràng: giảm 40% tốc đánh quái trong 260px, 8s',
      kind: 'debuff',
      manaCost: 35, cooldown: 15, unlockWave: 1,
      // Tin Đồn Chợ làm chậm CHẠY; chiêu này bóp tốc ĐÁNH ⇒ hai trục khác nhau, cộng
      // dồn được. Dáng 'taunt' (dang tay khiêu khích) khác 'shout' của 2 chiêu gốc.
      stat: 'atkSpeed', buffMult: 0.6, duration: 8, radius: 260, maxTargets: 99,
      anim: 'taunt',
    },
    {
      id: 'luoiCa',
      name: 'Trùm Lưới Cá',
      desc: 'Trùm lưới cá: giảm 70% tốc chạy trong 220px, 4s',
      kind: 'debuff',
      manaCost: 30, cooldown: 16, unlockWave: 1,
      // Khác hẳn Tin Đồn Chợ (giảm 45% trong 7s): chiêu này gần như GHIM CHÂN (0.3)
      // nhưng chỉ 4s ⇒ dùng để chặn boss lao vào Nhà, không phải để rỉa cả wave.
      stat: 'moveSpeed', buffMult: 0.3, duration: 4, radius: 220, maxTargets: 99,
      anim: 'hurl',
    },
  ],

  // ------------------------------------------------------------------ Bắn thuê
  army: [
    {
      id: 'khongKich',
      name: 'Gọi Không Kích',
      desc: 'Gọi không kích: 340% sát thương trong 180px',
      kind: 'aoeDamage',
      manaCost: 50, cooldown: 16, unlockWave: 1,
      // Lựu Đạn (2.6/130px) và Pháo Binh (3 quả 1.7) đều đánh nhỏ, lẻ. Chiêu này là
      // một quả BOM LỚN: mạnh nhất class nhưng đắt (50) và hồi lâu (16s).
      // Dáng 'summon' (hai tay giơ lên trời) = "gọi phi cơ" — hài mà đúng nghĩa.
      mult: 3.4, radius: 180, maxTargets: 12,
      anim: 'summon',
    },
    {
      id: 'haoChienDau',
      name: 'Đào Hào Chiến Đấu',
      desc: 'Đào hào: khiên 40% máu tối đa trong 9s',
      kind: 'shield',
      manaCost: 40, cooldown: 20, unlockWave: 1,
      // Bắn thuê gốc KHÔNG có chiêu phòng thủ nào (toàn sát thương + buff giáp) ⇒
      // đây là mảnh ghép bù đúng lỗ hổng: bị boss áp sát thì vẫn trụ được.
      pct: 0.4, duration: 9,
      anim: 'guard',
    },
    {
      id: 'danKhoi',
      name: 'Đạn Khói',
      desc: 'Ném đạn khói: giảm 40% sát thương quái trong 230px, 6s',
      kind: 'debuff',
      manaCost: 30, cooldown: 14, unlockWave: 1,
      // Đối xứng với Hào Chiến Đấu: một chiêu đỡ cho MÌNH, một chiêu bịt mắt QUÁI.
      stat: 'atk', buffMult: 0.6, duration: 6, radius: 230, maxTargets: 99,
      anim: 'hurl',
    },
  ],

  // ------------------------------------------------------------------ Đẻ mướn
  mother: [
    {
      id: 'goiHangXom',
      name: 'Gọi Hàng Xóm',
      desc: 'Gọi 4 hàng xóm sang đánh hộ (40s)',
      kind: 'summon',
      manaCost: 45, cooldown: 18, unlockWave: 1,
      // Sinh Ngay Một Đứa (3 con/30 mana/10s) và Gọi Cả Họ (6 con/55 mana/20s).
      // Chiêu này nằm GIỮA: 4 con, 45 mana, 18s ⇒ mốc đệm cho người chơi chưa đủ mana.
      count: 4, summonKind: 'brat', duration: 40,
      anim: 'summon',
    },
    {
      id: 'choiTreTroiGiang',
      name: 'Chổi Tre Trời Giáng',
      desc: 'Chổi tre giáng xuống: 290% sát thương trong 200px',
      kind: 'aoeDamage',
      manaCost: 45, cooldown: 14, unlockWave: 1,
      // Mẹ Nổi Giận (3.2/210px/50 mana/15s) là chiêu gốc mạnh nhất; chiêu này yếu hơn
      // chút nhưng RẺ và nhanh hơn ⇒ nhịp đánh khác, không phải bản nâng cấp.
      mult: 2.9, radius: 200, maxTargets: 12,
      anim: 'groundSlam',
    },
    {
      id: 'nuocMatCaSau',
      name: 'Nước Mắt Cá Sấu',
      desc: 'Khóc lóc ăn vạ, hồi ngay 420 máu',
      kind: 'heal',
      manaCost: 40, cooldown: 22, unlockWave: 1,
      // CẢNH BÁO: nhánh 'heal' của `World.castSkill` rẽ hai đường theo class —
      // class `usesAmmo` (Bắn thuê) hồi ĐẠN, class thường hồi MÁU. Đẻ mướn không
      // dùng đạn nên `amount: 420` là MÁU (không phải 420 viên đạn).
      amount: 420,
      anim: 'shout',
    },
  ],
};

/**
 * TOÀN BỘ chiêu 1 class CÓ THỂ dùng = 7 chiêu gốc (theo đúng thứ tự cũ) + 3 chiêu ghép.
 * Thứ tự quan trọng: 7 phần tử đầu PHẢI trùng `CLASSES[id].skills` để index cũ
 * (save, `skillCds`, UI) không lệch nghĩa.
 */
export function skillPool(classId: ClassId): SkillDef[] {
  const base = CLASSES[classId]?.skills ?? [];
  const extra = EXTRA_SKILLS[classId] ?? [];
  return [...base, ...extra];
}

/** Tìm chiêu theo id trong CẢ 5 pool. Không thấy ⇒ `undefined` (KHÔNG ném lỗi). */
export function skillById(id: string): SkillDef | undefined {
  for (const c of CLASS_ORDER) {
    const found = skillPool(c).find((s) => s.id === id);
    if (found) return found;
  }
  return undefined;
}

/** Class sở hữu 1 chiêu (kể cả chiêu gốc). Không thấy ⇒ `undefined`. */
export function classOfSkill(id: string): ClassId | undefined {
  for (const c of CLASS_ORDER) {
    if (skillPool(c).some((s) => s.id === id)) return c;
  }
  return undefined;
}

/** Bộ 7 chiêu MẶC ĐỊNH = ĐÚNG 7 chiêu gốc đang có (thứ tự y hệt `classes.ts`). */
/**
 * BỘ CHIÊU MẶC ĐỊNH khi ô trống — 3 chiêu đầu của class.
 *
 * ⚠️ PHẢI CẮT THEO `MAX_EQUIPPED_SKILLS`: bản cũ trả HẾT 7 chiêu gốc, mà trần nay là 3 ⇒
 * `validateLoadout` sẽ báo "Tối đa 3 chiêu (đang có 7)" và bộ chiêu mặc định trở thành KHÔNG HỢP LỆ
 * ⇒ người chơi vào trận là lỗi. Cắt tại nguồn để mọi đường (save mới, migrate, nút "mặc định") đúng.
 */
export function defaultLoadout(classId: ClassId): string[] {
  return (CLASSES[classId]?.skills ?? []).slice(0, MAX_EQUIPPED_SKILLS).map((s) => s.id);
}

/**
 * 3 chiêu CÓ THỂ GHÉP của class.
 *
 * Vì sao không lọc theo "đã sở hữu": hàm này là DANH MỤC để UI vẽ bảng ghép — chiêu
 * nào đã ghép rồi thì `craftedSkills[classId]` mới là nơi trả lời (xem `sim/craft.ts`).
 * Danh mục mà tự lọc theo state thì UI không hiện được "đã ghép".
 */
export function craftableSkills(classId: ClassId): SkillDef[] {
  return EXTRA_SKILLS[classId] ?? [];
}

/**
 * KIỂM TRA 1 BỘ TRANG BỊ: ≤7 ô · không trùng · mọi id có trong pool · tối thiểu 1 chiêu.
 *
 * Không ném lỗi với dữ liệu lạ (mảng rỗng, id rác, class rác) — luôn trả `{ok, reason}`.
 */
export function validateLoadout(classId: ClassId, ids: string[]): { ok: boolean; reason?: string } {
  if (!Array.isArray(ids)) return { ok: false, reason: 'Bộ trang bị không phải mảng' };
  if (ids.length === 0) return { ok: false, reason: 'Phải trang bị tối thiểu 1 chiêu' };
  if (ids.length > MAX_EQUIPPED_SKILLS) {
    return { ok: false, reason: `Tối đa ${MAX_EQUIPPED_SKILLS} chiêu (đang có ${ids.length})` };
  }
  const pool = skillPool(classId);
  if (pool.length === 0) return { ok: false, reason: `Class lạ: ${classId}` };
  const seen = new Set<string>();
  for (const id of ids) {
    if (typeof id !== 'string' || id.length === 0) return { ok: false, reason: 'Có ô trống trong bộ trang bị' };
    if (seen.has(id)) return { ok: false, reason: `Trùng chiêu: ${id}` };
    seen.add(id);
    if (!pool.some((s) => s.id === id)) {
      return { ok: false, reason: `Chiêu không thuộc class này: ${id}` };
    }
  }
  return { ok: true };
}
